import { useCallback } from 'react';
import { useCookie } from 'react-use';

import { startOfDay } from 'date-fns';
import isNil from 'lodash/isNil';

import { CookieName } from '@nocowanie/core/enums';
import { dateHelpers } from '@nocowanie/core/helpers';

import { parseCookies } from '../helpers';
import { SearchEngineModel } from '../models';

const decodeCookie = (cookieValue: string | null) => {
    return cookieValue ? JSON.parse(atob(cookieValue)) : {};
};

export const useSearchCookie = (domain: string) => {
    const [value, updateCookie] = useCookie(CookieName.SearchCriteria);
    const currentParams = decodeCookie(value);

    const setCookie = useCallback(
        (params: Partial<SearchEngineModel>) => {
            const adultsCount = params.guests?.adultsCount;
            const childrenCount = params.guests?.childrenCount || 0;

            const getInstantReservation = () => {
                if (!isNil(params.instantReservation)) {
                    return params.instantReservation ? '1' : '0';
                }

                return currentParams?.instantReservation;
            };

            const data = {
                miejsca_dorosli: adultsCount,
                miejsca_dzieci: childrenCount,
                miejsca: adultsCount ? adultsCount + childrenCount : undefined,
                wiek_dzieci: params.guests?.childrenAges?.join(','),
                rezerwacja_online: getInstantReservation(),
                flex_dates: params.flexDates,
                data:
                    params.dates?.checkInDate &&
                    params.dates.checkOutDate &&
                    dateHelpers.isValidRange(params.dates.checkInDate, params.dates.checkOutDate)
                        ? {
                              od: dateHelpers.format(params.dates.checkInDate, 'yyyy-MM-dd'),
                              do: dateHelpers.format(params.dates.checkOutDate, 'yyyy-MM-dd'),
                          }
                        : undefined,
                timestamp: Date.now(),
            };

            updateCookie(btoa(JSON.stringify(data)), {
                domain,
                expires: 7,
                secure: true,
            });
        },
        [updateCookie, domain, currentParams?.instantReservation],
    );

    const getSearchCriteriaFromCookie = useCallback((): Partial<SearchEngineModel> | null => {
        const cookies = parseCookies(document.cookie);
        const decoded = decodeCookie(cookies[CookieName.SearchCriteria]);

        if (!decoded || typeof decoded !== 'object') {
            return null;
        }

        const hasGuests =
            decoded.miejsca_dorosli !== undefined || decoded.miejsca_dzieci !== undefined;
        const hasDates = decoded?.data?.od && decoded?.data?.do;

        if (!hasGuests && !hasDates && decoded.flex_dates === undefined) {
            return null;
        }

        return {
            dates: decoded.data
                ? {
                      checkInDate: decoded.data.od ? startOfDay(new Date(decoded.data.od)) : null,
                      checkOutDate: decoded.data.do ? startOfDay(new Date(decoded.data.do)) : null,
                  }
                : { checkInDate: null, checkOutDate: null },
            guests:
                decoded.miejsca_dorosli !== undefined || decoded.miejsca_dzieci !== undefined
                    ? {
                          adultsCount:
                              decoded.miejsca_dorosli !== undefined
                                  ? Number(decoded.miejsca_dorosli)
                                  : 2,
                          childrenCount:
                              decoded.miejsca_dzieci !== undefined
                                  ? Number(decoded.miejsca_dzieci)
                                  : 0,
                          childrenAges:
                              decoded.wiek_dzieci && decoded.wiek_dzieci.trim().length > 0
                                  ? decoded.wiek_dzieci.split(',').map(Number)
                                  : [],
                      }
                    : undefined,
            flexDates: decoded.flex_dates,
        };
    }, []);

    return {
        setCookie,
        getSearchCriteriaFromCookie,
    };
};
